import React from 'react';
import {Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, LinearProgress, List, ListItem, ListItemText, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import * as CommunicationService from '../../../services/CommunicationService';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_TOTAL_SIZE = 20 * 1024 * 1024;

export default function TeregoTalonUploadDialog({open, order, onClose, onAccepted, translate = false}) {
    const [files, setFiles] = React.useState([]);
    const [consent, setConsent] = React.useState(true);
    const fileInput = React.useRef(null);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!open) {
            setFiles([]);
            setConsent(true);
            setBusy(false);
            setError(null);
        }
    }, [open]);

    const chooseFiles = (event) => {
        const selected = Array.from(event.target.files || []);
        setError(null);
        if (!selected.length) return;
        if (selected.length > 5 || selected.some(file => file.size > MAX_FILE_SIZE)
            || selected.reduce((sum, file) => sum + file.size, 0) > MAX_TOTAL_SIZE) {
            setError(translate ? 'Choose up to five files, with a maximum of 20 MB in total.' : 'Alege maximum cinci fișiere, cu maximum 20 MB în total.');
            return;
        }
        const hasPdf = selected.some(file => file.type === 'application/pdf');
        const hasUnsupported = selected.some(file => !['application/pdf', 'image/jpeg', 'image/png', 'image/webp'].includes(file.type));
        if (hasUnsupported || (hasPdf && selected.length > 1)) {
            setError(translate ? 'Upload one PDF or multiple JPEG, PNG or WEBP images.' : 'Încarcă un singur PDF sau mai multe imagini JPEG, PNG ori WEBP.');
            return;
        }
        setFiles(selected);
    };

    const submit = () => {
        if (!files.length || !consent || busy) return;
        setBusy(true);
        setError(null);
        CommunicationService.submitTeregoTalonIntake(order.id, files, consent, order.promotionAccessToken)
            .then(response => {
                if (response.accepted !== true) throw new Error(response.message || 'Talon intake was not accepted');
                onAccepted({...response, accepted: true});
            })
            .catch(() => {
                setBusy(false);
                setError(translate ? 'We could not save the document. Please try again.' : 'Documentul nu a putut fi salvat. Încearcă din nou.');
            });
    };

    return <>
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm"
        aria-labelledby="talon-upload-title"
        BackdropProps={{sx: {backgroundColor: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(6px)'}}}
        PaperProps={{sx: {borderRadius: {xs: '24px', sm: '32px'}, m: 2, width: 'calc(100% - 32px)', maxWidth: 640, boxShadow: '0 24px 80px rgba(15, 23, 42, 0.24)', color: '#172033'}}}>
        <DialogTitle id="talon-upload-title" sx={{display: 'flex', alignItems: 'flex-start', gap: 2, justifyContent: 'space-between', px: {xs: 2.5, sm: 4}, pt: 3, pb: 1.5}}>
            <Typography component="span" sx={{fontSize: {xs: 24, sm: 30}, fontWeight: 800, lineHeight: 1.2, letterSpacing: '-0.03em'}}>
                {translate ? 'Upload a registration certificate / previous insurance policy' : 'Încarcă talon / asigurare veche'}
            </Typography>
            <IconButton aria-label={translate ? 'Close' : 'Închide'} onClick={onClose} disabled={busy} size="small" sx={{bgcolor: '#f3f4f6', color: '#77808f', flexShrink: 0}}>
                <CloseIcon fontSize="small"/>
            </IconButton>
        </DialogTitle>
        <DialogContent sx={{px: {xs: 2.5, sm: 4}, pb: 1}}>
            <Typography variant="body2" sx={{mb: 2.5, color: '#77808f', lineHeight: 1.6}}>
                {translate ? 'You can upload either a registration certificate or a previous insurance policy. Terego will create an RCA request using your details and the uploaded document.' : 'Poți încărca talonul sau asigurarea veche. Terego va crea o solicitare RCA folosind datele completate și documentul încărcat.'}
            </Typography>
            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
            <Button onClick={() => fileInput.current?.click()} disabled={busy} fullWidth
                aria-label={translate ? 'Choose files' : 'Alege fișierele'}
                sx={{display: 'flex', flexDirection: 'column', alignItems: 'stretch', p: 0, overflow: 'hidden', textAlign: 'left', textTransform: 'none', color: '#172033', border: '1px solid #a6a1ff', borderRadius: '24px', background: 'linear-gradient(145deg, #f7fbff, #e9efff)', boxShadow: '0 8px 24px rgba(41, 66, 155, 0.08)', transition: 'box-shadow 180ms ease, border-color 180ms ease', '&:hover': {background: 'linear-gradient(145deg, #f7fbff, #e3eaff)', borderColor: '#7065ff', boxShadow: '0 12px 28px rgba(41, 66, 155, 0.16)'}, '&.Mui-focusVisible': {outline: '3px solid #7065ff', outlineOffset: 3}}}>
                <Box component="img" src="/talon-example.webp" alt="" sx={{display: 'block', width: '100%', height: {xs: 135, sm: 180}, objectFit: 'cover', objectPosition: 'center', opacity: 0.8, borderBottom: '1px solid #dce4f5'}}/>
                <Box component="span" sx={{display: 'block', p: {xs: 2, sm: 2.5}}}>
                    <Typography component="span" sx={{display: 'block', fontSize: {xs: 19, sm: 22}, fontWeight: 800, lineHeight: 1.3}}>
                        {translate ? 'Registration certificate / previous insurance policy' : 'Talon / asigurare veche'}
                    </Typography>
                    <Typography component="span" sx={{display: 'block', mt: 0.75, color: '#77808f', fontSize: 14, lineHeight: 1.5}}>
                        {translate ? 'Choose clear photos with all document details visible.' : 'Alege fotografii clare, în care toate datele documentului sunt vizibile.'}
                    </Typography>
                    <Box component="span" sx={{display: 'flex', alignItems: 'center', gap: 1, mt: 2, color: '#7065ff', fontSize: 14, fontWeight: 800}}>
                        <FileUploadOutlinedIcon fontSize="small"/>
                        {files.length ? (translate ? 'Change files' : 'Schimbă fișierele') : (translate ? 'Choose a photo or PDF' : 'Alege fotografia sau PDF-ul')}
                    </Box>
                </Box>
            </Button>
            <input ref={fileInput} hidden disabled={busy} type="file" multiple accept="image/jpeg,image/png,image/webp,application/pdf" onChange={chooseFiles}/>
            <Typography sx={{textAlign: 'center', color: '#77808f', fontSize: 12, mt: 2, mb: 1.5}}>
                {translate ? 'JPG, PNG, WEBP or PDF · Up to 5 images or one PDF · 10 MB per file, 20 MB total' : 'JPG, PNG, WEBP sau PDF · Maximum 5 imagini sau un PDF · 10 MB / fișier, 20 MB în total'}
            </Typography>
            {files.length > 0 && <List dense sx={{bgcolor: '#f7f8fc', borderRadius: '16px', mb: 2}}>
                {files.map(file => <ListItem key={`${file.name}-${file.size}`}>
                    <UploadFileIcon sx={{color: '#7065ff', mr: 1.5}}/>
                    <ListItemText primary={file.name} primaryTypographyProps={{sx: {overflowWrap: 'anywhere', fontSize: 14}}} secondary={`${Math.round(file.size / 1024)} KB`}/>
                </ListItem>)}
            </List>}
            <FormControlLabel
                sx={{m: 0, gap: 1.25, alignItems: 'center', '& .MuiFormControlLabel-label': {fontSize: 13, lineHeight: 1.6, color: '#677181'}}}
                control={<Checkbox checked={consent} onChange={event => setConsent(event.target.checked)} disabled={busy} sx={{p: 0, flexShrink: 0, color: '#a6a1ff', '&.Mui-checked': {color: '#7065ff'}}}/>}
                label={translate ? 'I agree to share these details and documents with Terego.' : 'Sunt de acord să transmit aceste date și documente către Terego.'}
            />
        </DialogContent>
        <DialogActions sx={{px: {xs: 2.5, sm: 4}, pt: 2, pb: 3, gap: 1, flexWrap: 'wrap'}}>
            <Button onClick={onClose} disabled={busy} sx={{textTransform: 'none', color: '#677181', borderRadius: '12px'}}>{translate ? 'Cancel' : 'Anulează'}</Button>
            <Button onClick={submit} variant="contained" disabled={busy || !files.length || !consent} sx={{textTransform: 'none', borderRadius: '12px', px: 2.5, py: 1.25, bgcolor: '#7065ff', boxShadow: 'none', '&:hover': {bgcolor: '#5d51e8', boxShadow: 'none'}}}>{translate ? 'Save and continue' : 'Salvează și continuă'}</Button>
        </DialogActions>
    </Dialog>
    <Dialog
        open={open && busy}
        onClose={() => {}}
        disableEscapeKeyDown
        aria-labelledby="terego-upload-progress-title"
        aria-describedby="terego-upload-progress-description"
        BackdropProps={{sx: {backgroundColor: 'rgba(15, 23, 42, 0.72)', backdropFilter: 'blur(8px)'}}}
        PaperProps={{sx: {borderRadius: '24px', p: {xs: 3, sm: 4}, width: 'calc(100% - 32px)', maxWidth: 420, textAlign: 'center', color: '#172033'}}}
    >
        <Typography id="terego-upload-progress-title" variant="h6" sx={{fontWeight: 800, mb: 1}}>
            {translate ? 'Uploading your document' : 'Se încarcă documentul'}
        </Typography>
        <Typography id="terego-upload-progress-description" variant="body2" sx={{color: '#77808f', lineHeight: 1.6, mb: 2.5}}>
            {translate ? 'We are sending your document and details to Terego. Please keep this window open.' : 'Trimitem documentul și datele tale către Terego. Te rugăm să păstrezi fereastra deschisă.'}
        </Typography>
        <LinearProgress sx={{borderRadius: 4, '& .MuiLinearProgress-bar': {backgroundColor: '#7065ff'}}}/>
    </Dialog>
    </>;
}
